/**
 * 组件点击时统一的数据提取与打包流程
 * 每个组件都走此函数：按 componentType 做 case，组织 current + 7天/30天/12月，再交给钩子发给 AI
 */

import type { StoreData } from '../hooks/useLoadingStoreData';
import type {
  ComponentDataPackage,
  TimeDirection,
  PriceSuggestionContext,
  OrderFlowSpeed,
} from '../types/aiAssistantTypes';
import type { ChartListItem } from './dataGenerator';
import { storeComponentUsesTimeDirection } from './storeComponentBundle';

/**
 * 从房态 dataSource 中计算价格建议上下文：整体出租率、剩余总房数、当前时间等
 * 与动态定价提示词（外部因素、订单流速、追随机制、房型策略）配套使用
 */
function buildPriceSuggestionContext(
  dataSource: ChartListItem[] | null,
  storeData: StoreData
): PriceSuggestionContext | undefined {
  if (!Array.isArray(dataSource) || dataSource.length === 0) return undefined;

  const now = new Date();
  const hour = now.getHours();
  const minute = now.getMinutes();

  const storeTotalRow = dataSource.find((r: any) => r.isStoreTotal);
  const overallOccupancy = storeTotalRow == null ? 0 : Number(storeTotalRow.progress) || 0;
  const remainingTotalRooms = storeTotalRow == null ? 0 : Number(storeTotalRow.quantityOpen) || 0;

  let orderFlowSpeed: OrderFlowSpeed = 'normal';
  const raw = storeData.orderTrafficBundle?.last24Hours ?? storeData.orderTraffic?.rawData ?? storeData.orderTraffic;
  if (raw && typeof raw === 'object') {
    const orders = (raw as any).orders ?? (raw as any).orderCount;
    if (Array.isArray(orders) && orders.length > 0) {
      const recent = orders.slice(-3).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
      const prev = orders.slice(-6, -3).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
      if (recent > prev * 1.2) orderFlowSpeed = 'fast';
      else if (prev > 0 && recent < prev * 0.8) orderFlowSpeed = 'slow';
    }
  }

  return {
    currentTime: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    overallOccupancy,
    remainingTotalRooms,
    orderFlowSpeed,
  };
}

/**
 * 按组件类型从 storeData 中提取并打包数据。
 * 内置多档包组件勿带 timeDirection（current 已含 last7Days 等键）。
 */
export function buildComponentDataPackage(
  storeId: string | number,
  componentType: string,
  storeData: StoreData,
  timeDirection: TimeDirection = 'past'
): ComponentDataPackage {
  const includeTimeDirection = storeComponentUsesTimeDirection(componentType);
  const emptyPackage = (current: any, extra?: Partial<ComponentDataPackage>): ComponentDataPackage => ({
    ...(includeTimeDirection ? { timeDirection } : {}),
    current,
    last7Days: undefined,
    last30Days: undefined,
    last12Months: undefined,
    ...extra,
  });

  switch (componentType) {
    case 'room-status': {
      const dataSource = storeData.roomStatus?.dataSource ?? null;
      const priceSuggestionContext = buildPriceSuggestionContext(dataSource, storeData);
      return emptyPackage(dataSource, priceSuggestionContext ? { priceSuggestionContext } : undefined);
    }
    case 'order-traffic':
      return emptyPackage(storeData.orderTrafficBundle ?? null);
    case 'room-status-recently':
      return emptyPackage(storeData.roomStatusRecently?.baseChartData ?? null);
    case 'price-alert':
      return emptyPackage((storeData as any)?.priceAlert ?? null);
    case 'revpar':
      return emptyPackage(storeData.revparBundle ?? null);
    case 'channel-info':
      return emptyPackage(storeData.channelInfoBundle ?? null);
    case 'channel-price':
      return emptyPackage(storeData.channelPrice?.rawData ?? null);
    case 'room-health':
      return emptyPackage(storeData.roomHealthBundle ?? null);
    case 'customer-source':
      return emptyPackage(storeData.customerSourceBundle ?? null);
    case 'discount-analysis':
      return emptyPackage(storeData.discountAnalysisBundle ?? null);
    case 'channel-analysis':
      return emptyPackage(storeData.channelAnalysisBundle ?? null);
    case 'occupancy-analysis':
      return emptyPackage(storeData.occupancyAnalysisBundle ?? null);
    case 'operation-efficiency':
      return emptyPackage(storeData.operationEfficiencyBundle ?? null);
    case 'store-overview':
      return emptyPackage(storeData.storeOverview ?? null);
    default:
      return emptyPackage(null);
  }
}
